
import { useUserContext } from "../../../../contexts/user-context/UserContext";
import { useQueryNotifications } from "../../../../queries/UseQueryNotifications";
import { useGroup } from "../../../../queries/groups/UseGroup";
import { useEffect, useMemo, useState } from "react";
import NoData from "../../../no-data/NoData";
import { useTranslation } from "react-i18next";
import { DashboardContainer, DashboardContainerType } from "../../../dashboard/dashboard-container/DashboardContainer";
import { useAlarmSetpointNotificationCounts } from "../../../../queries/alarm-data/UseAlarmSetpointNotificationCounts";
import { AlarmNotificationsType } from "../../../../services/alarm-data-service/Models";
import { CustomDurationDialogValues } from "../../../custom-duration-dialog/CustomDurationDialog";
import {  Box, Stack  } from "@mui/material";
import { useCustomerAttribute } from "../../../../queries/attributes/UseCustomerAttribute";
import { IeLynxObject } from "../../../../common/models/eLynxObject";
import { useAlarmNotificationDetail } from "../../../../queries/alarm-data/UseAlarmtNotificationDetail";
import { CellClickedEvent, ColDef } from "ag-grid-community";
import { RowDef } from "../../../ag-grid-extensions/models/RowDef";
import { GridData } from "../../../ag-grid-extensions/models/GridData";
import NotificationsDetailGridUtils, { notificationGridActionItems } from "./NotificationsDetailGridUtils";
import { useConfirmation } from "../../../../contexts/confirmation-context/ConfirmationContext";
import { getDeviceRoute, getGroupRoute, getTagRoute, getTankBatteryRoute, getTankRoute, getWellRoute } from "../../../../common/utilities/RouteHelpers";
import { UIViewEnum } from "../../../../common/enums/UIViewEnum";
import { isMobile, isTablet } from "react-device-detect";
import useNavigateTo, { NavigateToOptions } from "../../../../hooks/use-navigate-to/useNavigateTo";
import DefaultGrid from "../../../default-grid/DefaultGrid";
import { routesByViewID } from "../../../../routes/routes";
import { useUserDefaultViews } from "../../../../queries/user-default-view/UseUserDefaultViews";
import { eLynxObjectTypeEnum } from "../../../../common/enums/eLynxObjectType";
import { NewViewTypeEnum } from "../../../../common/enums/NewViewTypeEnum";


export type NotificationsDetailGridProps = {
    notificationsType: AlarmNotificationsType,
    groupID: number,
    durationOptions: CustomDurationDialogValues,
    showEmail: boolean,
    showVoice: boolean,
    showText: boolean,
    targetObject?: IeLynxObject
};

export function NotificationsDetailGrid({
    notificationsType,
    groupID,
    durationOptions,
    showEmail,
    showVoice,
    showText,
    targetObject
}: NotificationsDetailGridProps) {
    const isPhone = isMobile && !isTablet;
    const [gridData, setGridData] = useState<GridData>();
    const [userState] = useUserContext();
    const { t } = useTranslation();
    const { confirm } = useConfirmation();
    const navigateTo = useNavigateTo();


    const {data: group } = useQueryNotifications(useGroup)({
        customerID: userState.customer?.id as number,
        groupID
    });

    const {data, status } = useQueryNotifications(useAlarmSetpointNotificationCounts)({
        type: notificationsType,
        organizationID: group?.organizationID ?? -1,
        contextGroupID: groupID,
        startDate: durationOptions.duration.startDate as Date,
        endDate: durationOptions.duration.endDate as Date,
    });

    const {data: notificationDetail, status: notificationDetailStatus } = useQueryNotifications(useAlarmNotificationDetail)({
        organizationID: group?.organizationID ?? -1,
        contextGroupID: groupID,
        startDate: durationOptions.duration.startDate as Date,
        endDate: durationOptions.duration.endDate as Date,
    });


    const { data: allowVoiceAlarmAttribute } = useQueryNotifications(useCustomerAttribute)({
        groupID: groupID,
        name: "allow voice alarm",
    });

    const allowVoiceAlarm = allowVoiceAlarmAttribute
        ? allowVoiceAlarmAttribute.attributeValue.toLowerCase() === "true"
        : false;

    const { data: defaultViews } = useQueryNotifications(useUserDefaultViews)({
    });

    const viewID = useMemo(() => (objectType: eLynxObjectTypeEnum) => {
        return defaultViews?.find(o => o.objectType === eLynxObjectTypeEnum.Device && 
                    o.user?.id === userState.user?.id)?.viewID ?? 
                defaultViews?.find(o => o.objectType === eLynxObjectTypeEnum.Device && 
                !o.user)?.viewID ??
            UIViewEnum.Device_Details;

    }, [
        defaultViews,
        userState.user?.id
    ]);        

    async function onExecuteAction() {
        const result = await confirm({
            dialogTitle: t("alarmDashboard.alarmReport.gotoReport"),
            confirmationText: t("alarmDashboard.alarmReport.text"),
            yesButtonText: t("buttons.yesContinue"),
            noButtonText: t("buttons.noCancel"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        });

        if (result === "yes") {
            const url = getGroupRoute(UIViewEnum.AlarmsAndNotification_AlarmNotificationReport, groupID);
            navigateTo(url);
        }
    }

    function onCellClicked(event: CellClickedEvent): void {

        let url: string | undefined;

        const objectID = event.data?.objectID;
        const objectType = event.data?.objectType;

        if (event.colDef.field === "objectName") {

            const defaultViewID = viewID(objectType);

            
            const navigationViewID = objectType === eLynxObjectTypeEnum.Well ?
                        UIViewEnum.Well_Details_AlarmSetpoints : 
                        objectType === eLynxObjectTypeEnum.Tank ?
                        UIViewEnum.TankBattery_Details_AlarmSetpoints :
                        objectType === eLynxObjectTypeEnum.TankBattery ?
                        UIViewEnum.TankBattery_Details_AlarmSetpoints : 
                        UIViewEnum.Device_Details_AlarmSetpoints;


            const route = routesByViewID.get(navigationViewID);
            
            url = objectType === eLynxObjectTypeEnum.Well ?
                            getWellRoute(isPhone && !route.allowMobile ? 
                                        defaultViewID : 
                                        navigationViewID, 
                                        groupID, objectID)  :
                    objectType === eLynxObjectTypeEnum.Tank ?
                            getTankRoute(isPhone && !route.allowMobile ? 
                                        defaultViewID : 
                                        navigationViewID, 
                                        groupID, undefined, undefined, objectID)  :

                    objectType === eLynxObjectTypeEnum.TankBattery ?
                            getTankBatteryRoute(isPhone && !route.allowMobile ? 
                                        defaultViewID : 
                                        navigationViewID, 
                                        groupID, objectID)  :

                            getDeviceRoute(isPhone && !route.allowMobile ? 
                                        defaultViewID : 
                                        navigationViewID, 
                                        groupID, undefined, objectID);
         } else if (event.colDef.field === "tags") {
            const tagID = event.data?.tagID;

            const wellID = objectType === eLynxObjectTypeEnum.Well ?
                            objectID : undefined;
            const deviceID = objectType === eLynxObjectTypeEnum.Device ?
                            objectID : undefined;
            const tankID = objectType === eLynxObjectTypeEnum.Tank ?
                            objectID : undefined;
            const tankBatteryID = objectType === eLynxObjectTypeEnum.TankBattery ?
                            objectID : undefined;

            
            url = getTagRoute(UIViewEnum.Tag_Details_AlarmSetpoints,
                                groupID,
                                wellID,
                                deviceID,
                                tankBatteryID,
                                tankID,
                                tagID);
        }

        if (url) {
            let options: NavigateToOptions | undefined;

            const mouseEvent = event.event as MouseEvent;
            if (mouseEvent && (mouseEvent.shiftKey || mouseEvent.ctrlKey)) {
                options = {
                    newViewType: mouseEvent.shiftKey ? NewViewTypeEnum.NewWindow : NewViewTypeEnum.NewTab
                }
            }

            navigateTo(url, options);
        }

    };
    

    useEffect(() => {

        if (notificationDetail && data) {

            const cols: ColDef[] = NotificationsDetailGridUtils.createGridColumns();
            const rows: RowDef[] = NotificationsDetailGridUtils.createGridRows(notificationDetail, 
                data,
                notificationsType,
                targetObject?.displayText ?? '',
                showText,
                showEmail,
                showVoice && allowVoiceAlarm);

            if (cols && rows) {
                setGridData({
                    cols: cols,
                    rows: rows
                });
            }

            setGridData({
                rows: rows,
                cols: cols
            });
        }
    }, [
        data,
        setGridData,
        showText,
        showEmail,
        showVoice,
        allowVoiceAlarm,
        notificationDetail,
        notificationsType,
        targetObject
    ]);

    return (
        <>
            <DashboardContainer 
                dashboardContainerType={DashboardContainerType.Graph}
                title={t('alarmDashboard.alarmNotifications')}
                loading={status === 'loading' || notificationDetailStatus === 'loading'}
                actions={notificationGridActionItems}
                onExecuteAction={() => onExecuteAction()}
                sx={{
                    overflowY: isPhone ? 'hidden' : undefined
                }}
                >

                {!data ?
                    <NoData text={t('alarmDashboard.noData')} />
                    :
                    <>

                    <Stack
                        direction='row'
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            overflow: 'hidden',
                            justifyContent: 'flex-end',
                            marginBottom: 1
                        }}>
                    </Stack>                


                    <Box 
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            flexGrow: 1,
                            flexShrink: 1,
                            flexBasis: '100%',
                            //padding: theme.spacing(2)
                    }}>

                    <DefaultGrid
                        domLayout={isPhone ? 'autoHeight' : undefined}
                        suppressMovableColumns={true}
                        rowData={gridData?.rows}
                        suppressMultiSort={true}
                        defaultColDef={{
                            sortable: false,
                            filter: "agMultiColumnFilter",
                            resizable: true,
                            // SPR 04-08-2025 - Removed autoHeight and wrapText from defaultColDef
                            // Use multiline column type in column definitions, instead
                            cellStyle: {
                                wordBreak: 'normal',
                            }
                        }}
                        columnDefs={gridData?.cols}
                        onCellClicked={onCellClicked}
                    />

                    </Box>
                </>
                }

            </DashboardContainer>
        </>
    );
}
